import { type ButtonType } from "@common/controls/Button/component";
import { useI18n } from "@common/hooks/i18n";
import { Common } from "@common/types";
import { identity } from "lodash";
import React from "react";
import ConfirmModal, { type ChildrenPosition, type ConfirmTypeCustom } from "../Confirm/component";
import { type FormModalProps } from "../Form/component";
import withShow from "../hocs/withShow";
import withSubmit, { type WithSubmitProps } from "../hocs/withSubmit";

export interface DeleteModalProps<T = Common.GenericMap<any>> extends Omit<FormModalProps<T>, "cannotClose"> {
  buttonCaption?: string;
  buttonType?: ButtonType;
  cannotDelete?: boolean;
  childrenPosition?: ChildrenPosition;
  confirmText?: string;
  defaultChildrenData?: Common.GenericMap<any>;
  preventCloseWhenSubmitting?: boolean;
  message: string;
  onDelete: (data: T) => void | Promise<void>;
  onClose: () => void;
  isDeleting?: boolean;
  isStrict?: boolean;
  children?: any;
}

const DeleteModal = <T extends Common.GenericMap<any>>(props: DeleteModalProps<T>) => {
  const i18n = useI18n();

  const [_isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const { cannotDelete, confirmText, isDeleting, onDelete, errorMessage, buttonCaption, buttonType, ...modalProps } =
    props;
  const onConfirm = React.useCallback(
    async (data: T) => {
      setError(undefined);
      setIsDeleting(true);

      let r = onDelete(data);

      try {
        if (r instanceof Promise) await r;
      } catch (e) {
        setError(e.message);
      } finally {
        setIsDeleting(false);
      }
    },
    [setError, setIsDeleting, onDelete]
  );

  React.useEffect(() => {
    setError(undefined);
  }, [props.isVisible, setError]);

  const type = React.useMemo<ConfirmTypeCustom>(
    () => ({
      type: buttonType ?? "danger",
      children: buttonCaption ?? i18n.delete,
      confirmType: confirmText ?? "delete"
    }),
    [i18n.delete, confirmText, buttonCaption, buttonType]
  );

  return (
    <ConfirmModal
      {...modalProps}
      defaultChildrenData={props.defaultChildrenData}
      cannotConfirm={cannotDelete}
      confirmCaption={buttonCaption}
      errorMessage={error || errorMessage}
      type={type}
      onConfirm={onConfirm}
      isConfirming={isDeleting || _isDeleting}
    />
  );
};

export default DeleteModal;

export const DeleteModalWithShow = withShow<DeleteModalProps>(DeleteModal);

export type DeleteModalWithSubmitProps = Omit<DeleteModalProps, "onDelete" | "onClose"> & WithSubmitProps<any>;
export const DeleteModalWithSubmit = withSubmit<any, any, DeleteModalWithSubmitProps>(
  ({ submit, isSubmitting, onClose, ...props }: DeleteModalWithSubmitProps) => (
    <DeleteModal {...props} onClose={onClose ?? identity} onDelete={submit} isDeleting={isSubmitting} />
  )
);
export const DeleteModalWithShowSubmit = withShow(DeleteModalWithSubmit);
